import type { Maybe } from '@adornis/base/utilTypes.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import { Arg, Entity, Field, Query, Subscription } from '@adornis/baseql/decorators.js';
import { constructValue } from '@adornis/baseql/entities/construct.js';
import { getEntityByID } from '@adornis/baseql/operations/mongo.js';
import { getCollection, getCollectionHandle } from '@adornis/baseql/server/collections.js';
import { publishFilter } from '@adornis/baseql/server/context.js';
import { type BaseQLSelectionSet, selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { validate } from '@adornis/validation/decorators.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { GroupType } from '@chancenwerk/enums/enums.js';
import { catchError, from, of, switchMap } from 'rxjs';
import { CampusEvent } from './campus-learning-content/entities/CampusEvent.js';
import { CampusLesson } from './campus-learning-content/entities/CampusLesson.js';
import { CampusUserGroupFilter } from './filter/CampusUserGroupFilter.js';
import { Student } from './master-data-management/users/entities/Student.js';
import { ScopedEntity } from './ScopedEntity.js';

@Entity()
export class CampusUserGroup extends ScopedEntity {
  public static override _class = 'CampusStudentGroup';
  public static override _collectionName = 'campus-student-groups';

  static override get allFields() {
    return selectionSet(() => CampusUserGroup, 2);
  }

  @validate(nonOptional())
  @Field(() => String)
  public name: Maybe<string>;

  @Field(() => [String], { default: v => v ?? [] })
  public userIDs: Maybe<string[]>;

  @Field(() => String, {
    resolve: function (this: CampusUserGroup) {
      return async () => {
        return CampusUserGroup.getSchoolKuerzelForUserIDs(this.userIDs ?? []);
      };
    },
  })
  public kuerzel: Maybe<string>;

  public static async getSchoolKuerzelForUserIDs(userIDs: string[]) {
    if (!userIDs || userIDs?.length === 0) {
      return 'XXX_XXX';
    }

    for (const userID of userIDs) {
      const user = await Student.getByID<Student>(userID)({ kpfs: { school: { kuerzel: 1 } } });
      if (user?.kpfs?.[0]?.school?.kuerzel) {
        return user.kpfs[0].school.kuerzel;
      }
    }

    return 'XXX_XXX';
  }

  @Field(() => Int, { default: v => v ?? GroupType.STUDENTS })
  public groupType: Maybe<GroupType>;

  @Field(() => [String], { default: v => v ?? [] })
  public assignedLessonsIDs: Maybe<string[]>;

  @DataLoadedField(() => [CampusLesson], ({ assignedLessonsIDs }) => assignedLessonsIDs ?? [])
  public assignedLessons: Maybe<CampusLesson[]>;

  @Field(() => [String], { default: v => v ?? [] })
  public assignedEventIDs: Maybe<string[]>;

  @DataLoadedField(() => [CampusEvent], ({ assignedEventIDs }) => assignedEventIDs ?? [])
  public assignedEvents: Maybe<CampusEvent[]>;

  @Field(() => String)
  public kpfID: Maybe<string>;

  override toString() {
    return `${this.kuerzel} ${this.name}`;
  }

  @Subscription(() => [CampusUserGroup])
  public static subscribeAllGroupsForUserID(@Arg('userID', () => String) userID: string) {
    return (gqlFields: BaseQLSelectionSet<CampusUserGroup>) => {
      const selector = { userIDs: userID };
      const handle = getCollectionHandle<CampusUserGroup>(this._collectionName);
      return from(publishFilter(this._class)).pipe(
        switchMap(publishFilter => {
          return handle.watchQuery(publishFilter ? { $and: [publishFilter, selector] } : selector);
        }),
        catchError(e => {
          return of([] as CampusUserGroup[]);
        }),
      );
    };
  }

  @Subscription(() => [CampusUserGroup])
  static subscribeGroupsPaginated(
    @Arg('skip', () => Int) skip: number,
    @Arg('limit', () => Int) limit: number,
    @Arg('filter', () => CampusUserGroupFilter) filter: CampusUserGroupFilter,
  ) {
    return (gqlFields: BaseQLSelectionSet<CampusUserGroup>) => {
      if (!filter) return of([] as CampusUserGroup[]);
      const selector = filter.getMongoFilter();
      const handle = getCollectionHandle<CampusUserGroup>(this._collectionName);
      return from(publishFilter(this._class)).pipe(
        switchMap(publishFilter => {
          return handle.watchQuery(publishFilter ? { $and: [publishFilter, selector] } : selector, {
            ...filter.getMongoOptions().sort,
            skip: 0,
            limit: 1000,
          });
        }),
        switchMap(async groups => {
          if (skip > 0) {
            return [];
          }

          const users = await getEntityByID(Student, [
            ...new Set(groups.flatMap(c => c.userIDs).filter(Boolean) as string[]),
          ])({ _id: 1, vorname: 1 });

          // * We can use the fact that the publish filter will prevent fetching of users that we don't have access to.
          // * This means that we only need to check if there is a difference between the userIDs and the users we have fetched.
          let filtered = groups.filter(g => {
            return g.userIDs?.length === 0 || g.userIDs?.some(id => users.find(u => u._id === id));
          });

          if (filter.kuerzel && filter.kuerzel.length > 0) {
            const kuerzel: Record<string, string> = {};
            for (const g of groups) {
              const res = await CampusUserGroup.getSchoolKuerzelForUserIDs(g.userIDs ?? []);
              kuerzel[g._id] = res;
            }
            filtered = filtered.filter(g => {
              return kuerzel[g._id]?.toLowerCase().includes(filter.kuerzel?.toLowerCase() ?? '');
            });
          }

          return filtered;
        }),
        catchError(e => {
          return of([] as CampusUserGroup[]);
        }),
      );
    };
  }

  @Query(() => [CampusUserGroup])
  static getStudentGroupsForStudentID(@Arg('userID', () => String) userID: string) {
    return async (gqlFields: BaseQLSelectionSet<CampusUserGroup>) => {
      const col = await getCollection<CampusUserGroup>(this._class);
      const groups = col.find({ studentIDs: userID, groupType: GroupType.STUDENTS }).toArray();
      return groups;
    };
  }
}

export async function removeStudentFromCampusUserGroups(studentID: string) {
  const studentGroups = await CampusUserGroup.getStudentGroupsForStudentID(studentID)(CampusUserGroup.allFields);

  for (const studentGroup of studentGroups) {
    const group = constructValue(studentGroup);
    group.userIDs = group.userIDs?.filter(id => id !== studentID);
    await group.save();
  }
}
